import type { PlayerRole } from '../store/useGameStore';

export type CourtViewRotation = 0 | 90 | 180 | 270;

export interface CourtPosition {
  x: number;
  y: number;
}

export interface GridCell {
  zone: string;
  courtIndex: number;
}

export interface GridLayout {
  cols: number;
  rows: number;
  cells: GridCell[];
}

export interface RoleColorSet {
  bg: string;
  text: string;
  ring: string;
  badge: string;
  accent: string;
}

const ROLE_COLOR_MAP: Record<string, RoleColorSet> = {
  Passeur:  { bg: 'bg-cyan-500',    text: 'text-cyan-400',    ring: 'ring-cyan-400/50',    badge: 'bg-cyan-500/20 text-cyan-300',    accent: '#06b6d4' },
  R4:       { bg: 'bg-blue-500',    text: 'text-blue-400',    ring: 'ring-blue-400/50',    badge: 'bg-blue-500/20 text-blue-300',    accent: '#3b82f6' },
  Central:  { bg: 'bg-emerald-500', text: 'text-emerald-400', ring: 'ring-emerald-400/50', badge: 'bg-emerald-500/20 text-emerald-300', accent: '#10b981' },
  Pointu:   { bg: 'bg-rose-500',    text: 'text-rose-400',    ring: 'ring-rose-400/50',    badge: 'bg-rose-500/20 text-rose-300',    accent: '#f43f5e' },
  Lib:      { bg: 'bg-violet-500',  text: 'text-violet-400',  ring: 'ring-violet-400/50',  badge: 'bg-violet-500/20 text-violet-300',  accent: '#a855f7' },
};

const DEFAULT_ROLE_COLOR: RoleColorSet = {
  bg: 'bg-slate-500', text: 'text-slate-400', ring: 'ring-slate-400/50',
  badge: 'bg-slate-500/20 text-slate-300', accent: '#64748b',
};

export function getRoleColor(role: PlayerRole | string): RoleColorSet {
  return ROLE_COLOR_MAP[role] ?? DEFAULT_ROLE_COLOR;
}

export function getGridLayout(rotation: CourtViewRotation): GridLayout {
  switch (rotation) {
    case 0:
      return {
        cols: 3, rows: 2,
        cells: [
          { zone: '4', courtIndex: 3 }, { zone: '3', courtIndex: 2 }, { zone: '2', courtIndex: 1 },
          { zone: '5', courtIndex: 4 }, { zone: '6', courtIndex: 5 }, { zone: '1', courtIndex: 0 },
        ],
      };
    case 180:
      return {
        cols: 3, rows: 2,
        cells: [
          { zone: '1', courtIndex: 0 }, { zone: '6', courtIndex: 5 }, { zone: '5', courtIndex: 4 },
          { zone: '2', courtIndex: 1 }, { zone: '3', courtIndex: 2 }, { zone: '4', courtIndex: 3 },
        ],
      };
    case 90:
      return {
        cols: 2, rows: 3,
        cells: [
          { zone: '5', courtIndex: 4 }, { zone: '4', courtIndex: 3 },
          { zone: '6', courtIndex: 5 }, { zone: '3', courtIndex: 2 },
          { zone: '1', courtIndex: 0 }, { zone: '2', courtIndex: 1 },
        ],
      };
    case 270:
      return {
        cols: 2, rows: 3,
        cells: [
          { zone: '2', courtIndex: 1 }, { zone: '1', courtIndex: 0 },
          { zone: '3', courtIndex: 2 }, { zone: '6', courtIndex: 5 },
          { zone: '4', courtIndex: 3 }, { zone: '5', courtIndex: 4 },
        ],
      };
  }
}

const STANDARD_POSITIONS: CourtPosition[] = [
  { x: 80, y: 75 },
  { x: 80, y: 25 },
  { x: 50, y: 20 },
  { x: 20, y: 25 },
  { x: 20, y: 75 },
  { x: 50, y: 80 },
];

export function getStandardPositions(rotation: CourtViewRotation): CourtPosition[] {
  return STANDARD_POSITIONS.map(pos => rotateCourtPosition(pos, rotation));
}

export function getNetSide(rotation: CourtViewRotation): 'top' | 'bottom' | 'left' | 'right' {
  switch (rotation) {
    case 0:   return 'top';
    case 180: return 'bottom';
    case 90:  return 'right';
    case 270: return 'left';
  }
}

export function isHorizontalLayout(rotation: CourtViewRotation): boolean {
  return rotation === 90 || rotation === 270;
}

/**
 * Orientation des deux moitiés de terrain, **source de vérité unique** partagée
 * entre le terrain standard (rotation libre) et le terrain expert (toujours
 * horizontal : 90° ou 270°).
 *
 * `flipped` = notre équipe à DROITE. Correspondance vérifiée entre les deux modes :
 * à 90° le filet est à droite et notre camp à gauche (expert non retourné) ; à 270°
 * le filet est à gauche et notre camp à droite (expert retourné). Passer d'un mode à
 * l'autre ne doit jamais faire changer une équipe de côté.
 */
export function isFlippedRotation(rotation: CourtViewRotation): boolean {
  return rotation === 180 || rotation === 270;
}

/** Rotation horizontale correspondant à une orientation donnée. */
export function horizontalRotationFor(flipped: boolean): CourtViewRotation {
  return flipped ? 270 : 90;
}

// Formations de réception 5-1
// Lineup type : [Passeur, R4, Central, Pointu, R4, Central]
// Index 0=P1, 1=P2, 2=P3, 3=P4, 4=P5, 5=P6
// x: 0=gauche, 100=droite  |  y: 0=filet, 100=fond
//
// Contraintes réglementaires respectées :
//   P4.x < P3.x < P2.x  (ordre gauche→droite avant)
//   P5.x < P6.x < P1.x  (ordre gauche→droite arrière)
//   P4.y < P5.y, P3.y < P6.y, P2.y < P1.y  (avant devant arrière)
//
// R4 avant : x=20, y=70 (sauf formation 0 → x=80, y=70)
// Récepteur milieu : x=50, y=75
// Récepteur le plus à droite (hors P1) : x=80, y=70
const RECEPTION_FORMATIONS: Record<number, CourtPosition[]> = {
  // Passeur P1 : [Passeur, R4▲, Central, Pointu, R4, Central→Lib●]
  // Réceptionneurs : R4▲(P2)→droite, Lib(P6●)→milieu, R4(P5)→gauche
  0: [
    { x: 90, y: 65 }, // Passeur
    { x: 80, y: 55 }, // R4▲
    { x: 50, y: 35 }, // Central
    { x: 10, y: 40 }, // Pointu
    { x: 25, y: 75 }, // R4
    { x: 60, y: 75 }, // Central→Lib
  ],
  // Passeur P2 : [Central→Lib, Passeur, R4▲, Central, Pointu, R4]
  // Réceptionneurs : R4▲(P3)→gauche, R4(P6)→droite, Lib(P1)→naturel P1
  1: [
    { x: 80, y: 70 }, // Central→Lib
    { x: 65, y: 10 }, // Passeur
    { x: 20, y: 70 }, // R4▲
    { x: 15, y: 35 }, // Central
    { x: 35, y: 90 }, // Pointu
    { x: 50, y: 75 }, // R4
  ],
  // Passeur P3 : [R4, Central, Passeur, R4▲, Central→Lib, Pointu]
  // Réceptionneurs : R4▲(P4)→gauche, Lib(P5)→droite, R4(P1)→naturel P1
  2: [
    { x: 80, y: 70 }, // R4
    { x: 80, y: 35 }, // Central
    { x: 65, y: 10 }, // Passeur
    { x: 20, y: 70 }, // R4▲
    { x: 50, y: 75 }, // Central→Lib
    { x: 65, y: 90 }, // Pointu
  ],
  // Passeur P4 : [Pointu, R4▲, Central, Passeur, R4, Central→Lib]
  // Réceptionneurs : R4▲(P2)→gauche, R4(P5)→milieu, Lib(P6)→droite
  3: [
    { x: 90, y: 90 }, // Pointu
    { x: 20, y: 70 }, // R4▲
    { x: 15, y: 35 }, // Central
    { x: 10, y: 12 }, // Passeur
    { x: 50, y: 75 }, // R4
    { x: 80, y: 70 }, // Central→Lib
  ],
  // Passeur P5 : [Central→Lib, Pointu, R4▲, Central, Passeur, R4]
  // Réceptionneurs : R4▲(P3)→gauche, R4(P6)→droite, Lib(P1)→naturel P1
  4: [
    { x: 80, y: 70 }, // Central→Lib
    { x: 78, y: 15 }, // Pointu
    { x: 20, y: 70 }, // R4▲
    { x: 15, y: 35 }, // Central
    { x: 45, y: 40 }, // Passeur
    { x: 50, y: 75 }, // R4
  ],
  // Passeur P6 : [R4, Central, Pointu, R4▲, Central→Lib, Passeur]
  // Réceptionneurs : R4▲(P4)→gauche, Lib(P5)→droite, R4(P1)→naturel P1
  5: [
    { x: 80, y: 70 }, // R4
    { x: 80, y: 35 }, // Central
    { x: 70, y: 10 }, // Pointu
    { x: 20, y: 70 }, // R4▲
    { x: 50, y: 75 }, // Central→Lib
    { x: 60, y: 15 }, // Passeur
  ],
};

// Variante de la réception P1 quand la tactique fait attaquer le Pointu en zone 2
// (`teamTactics.p1AttackZone2 === 'Pointu'`) : le Pointu (P4) se colle au filet côté
// gauche pour croiser derrière le passeur vers la 2, et le R4 avant (P2) réceptionne
// dans le couloir gauche puisque c'est lui qui attaquera en 4.
// La variante « Pointu en 4 » (tactique par défaut `p1AttackZone2 === 'R4'`) reste
// `RECEPTION_FORMATIONS[0]`, inchangée.
//
// Mêmes contraintes réglementaires que RECEPTION_FORMATIONS :
//   P4.x < P3.x < P2.x, P5.x < P6.x < P1.x,
//   P4.y < P5.y, P3.y < P6.y, P2.y < P1.y
// Réceptionneurs : R4▲(P2)→gauche, R4(P5)→milieu, Lib(P6)→droite.
const RECEPTION_FORMATION_P1_POINTU_ZONE_2: CourtPosition[] = [
  { x: 92, y: 80 }, // Passeur — fond droite, pénètre en zone 2/3
  { x: 25, y: 75 }, // R4▲ — couloir gauche, enchaîne son attaque en 4
  { x: 20, y: 35 }, // Central — au filet, prêt pour la rapide
  { x: 10, y: 25 }, // Pointu — collé au filet à gauche, croise vers la zone 2
  { x: 55, y: 80 }, // R4 — couloir central
  { x: 80, y: 75 }, // Central→Lib — couloir droit
];

export function getReceptionFormation(
  setterCourtIndex: number,
  options?: { pointuAttacksZone2?: boolean },
): CourtPosition[] {
  // Ne pas substituer une autre rotation : `court[i]` doit rester aligné avec
  // `RECEPTION_FORMATIONS[setterCourtIndex][i]`. Seule la P1 a deux variantes,
  // pilotées par la tactique d'attaque en zone 2.
  if (setterCourtIndex === 0 && options?.pointuAttacksZone2) {
    return RECEPTION_FORMATION_P1_POINTU_ZONE_2;
  }
  return RECEPTION_FORMATIONS[setterCourtIndex] ?? RECEPTION_FORMATIONS[0];
}

// Positions de base pendant le point : grille unique zones I–VI ; le nom affiché par case
// vient de `visualCourt` / `court` (rotation réelle). Exceptions tactiques : réception P1,
// libéro / central (voir `courtVisual.ts`), pas de décalage par index de passeur ici.
const BASE_GAME_FORMATION: CourtPosition[] = [
  { x: 80, y: 75 },
  { x: 80, y: 25 },
  { x: 50, y: 20 },
  { x: 20, y: 25 },
  { x: 20, y: 75 },
  { x: 50, y: 80 },
];

export function getGameFormation(_setterCourtIndex: number): CourtPosition[] {
  void _setterCourtIndex;
  return BASE_GAME_FORMATION;
}

export function rotateCourtPosition(pos: CourtPosition, rotation: CourtViewRotation): CourtPosition {
  switch (rotation) {
    case 0:   return pos;
    case 180: return { x: 100 - pos.x, y: 100 - pos.y };
    case 90:  return { x: 100 - pos.y, y: pos.x };
    case 270: return { x: pos.y, y: 100 - pos.x };
  }
}

export function rotateZoneGrid(baseGrid: string[][], rotation: CourtViewRotation): string[][] {
  switch (rotation) {
    case 0:
      return baseGrid;
    case 180:
      return baseGrid.map(row => [...row].reverse()).reverse();
    case 90:
      return [0, 1, 2].map(i => [0, 1, 2].map(j => baseGrid[2 - j][i]));
    case 270:
      return [0, 1, 2].map(i => [0, 1, 2].map(j => baseGrid[j][2 - i]));
  }
}

export type OutZonePosition = 'top' | 'bottom' | 'left' | 'right';

export interface OutZonesLayout {
  filet: OutZonePosition;
  fond: OutZonePosition;
  gauche: OutZonePosition;
  droite: OutZonePosition;
}

export function getOutZonesLayout(rotation: CourtViewRotation): OutZonesLayout {
  switch (rotation) {
    case 0:
      return { filet: 'top', fond: 'bottom', gauche: 'left', droite: 'right' };
    case 180:
      return { filet: 'bottom', fond: 'top', gauche: 'right', droite: 'left' };
    case 90:
      return { filet: 'right', fond: 'left', gauche: 'top', droite: 'bottom' };
    case 270:
      return { filet: 'left', fond: 'right', gauche: 'bottom', droite: 'top' };
  }
}

export const NEXT_ROTATION: Record<CourtViewRotation, CourtViewRotation> = {
  0: 90,
  90: 180,
  180: 270,
  270: 0,
};
